import { useState } from 'react';
import { ApiError } from '../../lib/api.js';
import { downloadDocument, generateLessonPdf } from '../../lib/documents-api.js';
import { t } from '../../lib/i18n.js';

/**
 * "Download as PDF" for a saved lesson (spec §13.8). The PDF is made on the
 * server from the lesson as it is saved there, so it needs a signal — and an
 * unsaved change on this device is not in it, which the button says.
 */
export function LessonPdfButton({ lessonId, referenceNo, online }: { lessonId: string; referenceNo: string; online: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const { document } = await generateLessonPdf(lessonId);
      await downloadDocument(document.id, `${referenceNo}.pdf`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t("The PDF could not be made."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
      <button
        type="button"
        disabled={busy || !online}
        onClick={() => void download()}
        title={online ? undefined : t("Making a PDF needs a connection.")}
        className="rounded-sm border border-border-strong px-4 py-2 font-medium disabled:opacity-50"
      >
        {busy ? t("Making the PDF…") : t("Download as PDF")}
      </button>
      <span className="text-xs text-content-secondary">{t("As saved on the server; unsaved changes on this device are not in it.")}</span>
      {error && (
        <p role="alert" className="m-0 w-full" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </p>
      )}
    </div>
  );
}
